import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import OperationsHub from "../OperationsHub";

import raw from "../../../../_data/roadmap.json";

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const ZOOM_STEP = 0.15;

function normalizeLaneId(lane) {
  return String(lane || "")
    .trim()
    .toUpperCase();
}

function parseParents(parent) {
  if (!parent) return [];
  if (Array.isArray(parent))
    return parent
      .map(String)
      .map((s) => s.trim())
      .filter(Boolean);
  return String(parent)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function toBool(v) {
  if (typeof v === "boolean") return v;
  return (
    String(v || "")
      .trim()
      .toLowerCase() === "true"
  );
}

// FY months Oct..Sep
const FY_MONTHS = [
  { label: "Oct", monthNum: 10 },
  { label: "Nov", monthNum: 11 },
  { label: "Dec", monthNum: 12 },
  { label: "Jan", monthNum: 1 },
  { label: "Feb", monthNum: 2 },
  { label: "Mar", monthNum: 3 },
  { label: "Apr", monthNum: 4 },
  { label: "May", monthNum: 5 },
  { label: "Jun", monthNum: 6 },
  { label: "Jul", monthNum: 7 },
  { label: "Aug", monthNum: 8 },
  { label: "Sep", monthNum: 9 },
];

const monthIndexByCalendar = {
  10: 0,
  11: 1,
  12: 2,
  1: 3,
  2: 4,
  3: 5,
  4: 6,
  5: 7,
  6: 8,
  7: 9,
  8: 10,
  9: 11,
};

const QUARTER_INDEX = { Q1: 0, Q2: 1, Q3: 2, Q4: 3 };

// Matches the legacy roadmap.js date interpretation: month 1-3 means
// "the Nth month within the quarter named in the quarter field" (so the
// quarter field is authoritative), while month 4-12 is a literal calendar
// month remapped through the Oct-start fiscal year table. Milestones whose
// month/quarter fields disagree (e.g. month: 3, quarter: "Q4") position
// differently depending on which rule is applied, so this has to match
// roadmap.js exactly or nodes reorder relative to each other.
function fiscalMonthIndex(month, quarter) {
  const qIdx = QUARTER_INDEX[quarter] ?? 0;

  if (Number.isFinite(month) && month >= 1 && month <= 3) {
    return qIdx * 3 + (month - 1);
  }
  if (Number.isFinite(month) && month >= 1 && month <= 12) {
    return monthIndexByCalendar[month];
  }
  return qIdx * 3 + 1;
}

function monthStartX(yearStartX, yearWidth, monthIndex0to11) {
  const monthW = yearWidth / 12;
  return yearStartX + monthW * monthIndex0to11;
}

function monthCenterX(yearStartX, yearWidth, monthIndex0to11) {
  const monthW = yearWidth / 12;
  return yearStartX + monthW * (monthIndex0to11 + 0.5);
}

function weekCenterX(yearStartX, yearWidth, monthIndex0to11, week1to4) {
  const monthW = yearWidth / 12;
  const weekW = monthW / 4;
  return yearStartX + monthW * monthIndex0to11 + weekW * (week1to4 - 0.5);
}

function weekBoundaryX(yearStartX, yearWidth, monthIndex0to11, boundaryAfterWeek1to3) {
  const monthW = yearWidth / 12;
  const weekW = monthW / 4;
  return yearStartX + monthW * monthIndex0to11 + weekW * boundaryAfterWeek1to3;
}

function curvePath(x1, y1, x2, y2) {
  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);

  // Connections that jump across many lanes (large dy relative to dx) get a
  // tighter horizontal pull so they read as a clean diagonal instead of a
  // wide loop sweeping through unrelated lanes in between.
  const steepness = dy / (dx + dy || 1); // 0 = flat, 1 = vertical
  const basePull = clamp(dx * 0.35, 60, 420);
  const pull = basePull * (1 - steepness * 0.5);

  const c1x = x1 + pull;
  const c2x = x2 - pull;
  return `M ${x1} ${y1} C ${c1x} ${y1}, ${c2x} ${y2}, ${x2} ${y2}`;
}

function useGrabScroll(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    const onDown = (e) => {
      if (e.button !== 0) return;
      isDown = true;
      el.classList.add("is-grabbing");
      startX = e.clientX;
      startY = e.clientY;
      startLeft = el.scrollLeft;
      startTop = el.scrollTop;
    };

    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      el.scrollLeft = startLeft - dx;
      el.scrollTop = startTop - dy;
    };

    const onUp = () => {
      isDown = false;
      el.classList.remove("is-grabbing");
    };

    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [ref]);
}

function ensureModalRoot() {
  const existing = document.getElementById("its-roadmap-modal-root");
  if (existing) return existing;
  return null;
}

function resolveNodeImageSrc(image) {
  if (!image) return null;

  const s = String(image).trim();
  if (!s) return null;

  if (s.startsWith("http://") || s.startsWith("https://")) return s;

  if (s.startsWith("/")) return s;

  return `/assets/roadmap/images/${s}`;
}

function Modal({ open, node, onClose }) {
  const host = ensureModalRoot() || document.body;
  const [imgOk, setImgOk] = useState(true);

  useEffect(() => {
    setImgOk(true);
  }, [node?.id]);

  if (!open || !node) return null;

  const imgSrc = imgOk ? resolveNodeImageSrc(node.image) : null;

  return createPortal(
    <div className="itsr-modal-overlay" onMouseDown={onClose}>
      <div className="itsr-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="itsr-modal-header">
          <div className="itsr-modal-title">{node.title}</div>
          <button className="itsr-modal-close" onClick={onClose} type="button">
            ×
          </button>
        </div>

        <div className="itsr-modal-body">
          {imgSrc ? (
            <div className="itsr-modal-media">
              <img
                className="itsr-modal-img"
                src={imgSrc}
                alt={node.title || "Roadmap item"}
                onError={() => setImgOk(false)}
                loading="lazy"
              />
            </div>
          ) : null}

          <div className="itsr-field">
            <div className="itsr-fieldLabel">Lane</div>
            <div className="itsr-fieldVal">{node.laneLabel || node.lane}</div>
          </div>

          <div className="itsr-field">
            <div className="itsr-fieldLabel">Timeline</div>
            <div className="itsr-fieldVal">
              {node.year} • {node.quarter || "—"} • Month {node.month} • Week {node.week}
            </div>
          </div>

          <div className="itsr-field">
            <div className="itsr-fieldLabel">Level</div>
            <div className="itsr-fieldVal">{node.level || "—"}</div>
          </div>

          {node.parents?.length ? (
            <div className="itsr-field">
              <div className="itsr-fieldLabel">Parents</div>
              <div className="itsr-fieldVal">{node.parents.join(", ")}</div>
            </div>
          ) : null}

          {node.description ? (
            <div className="itsr-field">
              <div className="itsr-fieldLabel">Description</div>
              <div className="itsr-fieldVal">{node.description}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    host,
  );
}

export default function ITSRoadmap() {
  const lanesRaw = raw?.lanes || [];
  const milestonesRaw = raw?.milestones || [];

  const startYearCfg = Number(raw?.startYear);
  const endYearCfg = Number(raw?.endYear);

  const lanes = useMemo(() => {
    const baseY = 170;
    const stepY = 80;

    return (lanesRaw || []).map((l, i) => {
      const id = normalizeLaneId(l.id || l.label);
      return {
        id,
        label: String(l.label || id),
        color: String(l.color || "#5236ab"),
        y: Number.isFinite(Number(l.y)) ? Number(l.y) : baseY + stepY * i,
      };
    });
  }, [lanesRaw]);

  const milestones = useMemo(() => {
    const seen = new Map();

    return (milestonesRaw || [])
      .map((m) => {
        const rawId = String(m.id || "").trim();
        if (!rawId) return null;

        const count = (seen.get(rawId) || 0) + 1;
        seen.set(rawId, count);
        const id = count === 1 ? rawId : `${rawId}__${count}`;

        const lane = normalizeLaneId(m.lane);
        const year = Number(m.year);
        const quarter = String(m.quarter || "")
          .trim()
          .toUpperCase();
        const month = Number(m.month);
        const week = Number(m.week);

        const parents = parseParents(m.parent);

        const branchOnly = toBool(m.branchOnly) || toBool(m.bbranchOnly) || toBool(m.branch_only);

        const level = String(m.level || m.size || "minor")
          .trim()
          .toLowerCase();

        return {
          ...m,
          id,
          rawId,
          title: String(m.title || "").trim(),
          lane,
          year,
          quarter,
          month,
          week,
          parents,
          branchOnly,
          level,
          description: m.description ? String(m.description) : "",
        };
      })
      .filter(Boolean);
  }, [milestonesRaw]);

  const years = useMemo(() => {
    const set = new Set();
    milestones.forEach((m) => {
      if (Number.isFinite(m.year)) set.add(m.year);
    });

    let list = Array.from(set).sort((a, b) => a - b);

    if (Number.isFinite(startYearCfg) && Number.isFinite(endYearCfg)) {
      list = [];
      for (let y = startYearCfg; y <= endYearCfg; y++) list.push(y);
    }

    return list.length ? list : [new Date().getFullYear()];
  }, [milestones, startYearCfg, endYearCfg]);

  const startYear = Number.isFinite(startYearCfg) ? startYearCfg : years[0];
  const endYear = Number.isFinite(endYearCfg) ? endYearCfg : years[years.length - 1];

  const [jumpYear, setJumpYear] = useState(startYear);
  const [zoom, setZoom] = useState(1);
  const [query, setQuery] = useState("");
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [activeSuggest, setActiveSuggest] = useState(0);
  const [legendOpen, setLegendOpen] = useState(false);

  const [lanesOpen, setLanesOpen] = useState(false);
  const [enabledLaneIds, setEnabledLaneIds] = useState(() => new Set());

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoverNode, setHoverNode] = useState(null);
  const [highlightNodeId, setHighlightNodeId] = useState(null);

  const canvasRef = useRef(null);
  const laneOverlayRef = useRef(null);
  const searchWrapRef = useRef(null);
  const lanesWrapRef = useRef(null);

  useGrabScroll(canvasRef);

  useEffect(() => {
    const scroller = canvasRef.current;
    const overlay = laneOverlayRef.current;
    if (!scroller || !overlay) return;

    const onScroll = () => {
      overlay.style.transform = `translateY(${-scroller.scrollTop}px)`;
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  // init enabled lanes to "all"
  useEffect(() => {
    if (!lanes.length) return;
    setEnabledLaneIds((prev) => {
      if (prev && prev.size) return prev;
      return new Set(lanes.map((l) => l.id));
    });
  }, [lanes]);

  const lanesDisplay = useMemo(() => {
    const baseY = 170;
    const stepY = 80;
    const enabled =
      enabledLaneIds && enabledLaneIds.size ? enabledLaneIds : new Set(lanes.map((l) => l.id));

    return lanes.map((l, i) => ({
      ...l,
      y: baseY + stepY * i,
      enabled: enabled.has(l.id),
    }));
  }, [lanes, enabledLaneIds]);

  const laneById = useMemo(() => {
    const m = new Map();
    lanesDisplay.forEach((l) => m.set(l.id, l));
    return m;
  }, [lanesDisplay]);

  const layout = useMemo(() => {
    const leftGutter = 170;
    const rightPad = 90;

    const topGutter = 28;
    const axisHeight = 40;

    const yearWidth = 1600;

    const yearsCount = endYear - startYear + 1;
    const svgWidth = leftGutter + yearWidth * yearsCount + rightPad;

    const enabled =
      enabledLaneIds && enabledLaneIds.size ? enabledLaneIds : new Set(lanes.map((l) => l.id));

    const visible = milestones.filter((m) => {
      if (!enabled.has(m.lane)) return false;
      return true;
    });

    const baseLaneY = Math.min(...lanesDisplay.map((l) => l.y), 170);

    const nodes = visible
      .map((m) => {
        const lane = laneById.get(m.lane);
        if (!lane) return null;

        const monthIndex = fiscalMonthIndex(m.month, m.quarter);

        const w = Number.isFinite(m.week) ? clamp(m.week, 1, 4) : 2;

        const absYearStartX = leftGutter + yearWidth * (m.year - startYear);
        const x = weekCenterX(absYearStartX, yearWidth, monthIndex, w);

        const isMajor = m.level === "major" || m.level === "goal";
        const r = isMajor ? 13 : 10;

        return {
          ...m,
          x,
          y: topGutter + axisHeight + (lane.y - baseLaneY) + 40,
          r,
          color: lane.color || "#5236ab",
          laneLabel: lane.label,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.x - b.x);

    const nodesByRawId = new Map();
    const nodesById = new Map();

    nodes.forEach((n) => {
      nodesById.set(n.id, n);
      if (n.rawId && !nodesByRawId.has(n.rawId)) nodesByRawId.set(n.rawId, n);
    });

    const edges = [];
    nodes.forEach((n) => {
      if (!n.parents?.length) return;

      n.parents.forEach((pid, idx) => {
        const parentNode = nodesByRawId.get(pid) || nodesById.get(pid) || null;
        if (!parentNode) return;

        const supporting = idx > 0;
        const dashed = n.branchOnly || supporting;

        edges.push({
          id: `${pid}->${n.id}#${idx}`,
          from: parentNode,
          to: n,
          dashed,
        });
      });
    });

    const maxLaneY = Math.max(...lanesDisplay.map((l) => l.y), baseLaneY + 1);
    const contentH = maxLaneY - baseLaneY + 60;
    const svgHeight = topGutter + axisHeight + contentH + 50;

    return {
      leftGutter,
      rightPad,
      topGutter,
      axisHeight,
      yearWidth,
      svgWidth,
      svgHeight,
      nodes,
      edges,
      baseLaneY,
      lanesDisplay,
    };
  }, [milestones, lanes, lanesDisplay, laneById, query, startYear, endYear, enabledLaneIds]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    return layout.nodes.filter((n) => (n.title || "").toLowerCase().includes(q)).slice(0, 10);
  }, [query, layout.nodes]);

  const scrollToNode = (n) => {
    const scroller = canvasRef.current;
    if (!scroller || !n) return;

    const left = Math.max(0, n.x - 260);
    const top = Math.max(0, n.y - 220);
    scroller.scrollLeft = left;
    scroller.scrollTop = top;
  };

  const chooseSuggestion = (n) => {
    if (!n) return;
    setSelectedNode(null);
    setHoverNode(n);
    setQuery(n.title || "");
    setSuggestOpen(false);
    setActiveSuggest(0);
    setHighlightNodeId(n.id);
    scrollToNode(n);
  };

  useEffect(() => {
    if (!highlightNodeId) return;
    const t = window.setTimeout(() => setHighlightNodeId(null), 1800);
    return () => window.clearTimeout(t);
  }, [highlightNodeId]);

  useEffect(() => {
    const onDown = (e) => {
      const t = e.target;
      if (searchWrapRef.current && !searchWrapRef.current.contains(t)) {
        setSuggestOpen(false);
      }
      if (lanesWrapRef.current && !lanesWrapRef.current.contains(t)) {
        setLanesOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const x = layout.leftGutter + layout.yearWidth * (jumpYear - startYear);
    el.scrollLeft = Math.max(0, x - 220);
  }, [jumpYear, layout.leftGutter, layout.yearWidth, startYear]);

  // connected highlighting on hover
  // Dashed (supporting/branch) edges first so they sit behind the solid
  // main-path edges, which read more clearly on top.
  const sortedEdges = useMemo(() => {
    return [...layout.edges].sort((a, b) => (a.dashed === b.dashed ? 0 : a.dashed ? -1 : 1));
  }, [layout.edges]);

  const related = useMemo(() => {
    if (!hoverNode) return null;

    const adj = new Map();
    layout.nodes.forEach((n) => adj.set(n.id, new Set()));
    layout.edges.forEach((e) => {
      if (!adj.has(e.from.id)) adj.set(e.from.id, new Set());
      if (!adj.has(e.to.id)) adj.set(e.to.id, new Set());
      adj.get(e.from.id).add(e.to.id);
      adj.get(e.to.id).add(e.from.id);
    });

    const visited = new Set();
    const q = [hoverNode.id];
    visited.add(hoverNode.id);

    while (q.length) {
      const id = q.shift();
      const next = adj.get(id);
      if (!next) continue;
      next.forEach((nid) => {
        if (!visited.has(nid)) {
          visited.add(nid);
          q.push(nid);
        }
      });
    }

    const edgeIds = new Set();
    layout.edges.forEach((e) => {
      if (visited.has(e.from.id) && visited.has(e.to.id)) edgeIds.add(e.id);
    });

    return { nodeIds: visited, edgeIds };
  }, [hoverNode, layout.nodes, layout.edges]);

  const Axis = () => {
    const { topGutter, axisHeight, yearWidth, leftGutter } = layout;

    const yYear = 14;
    const yMonth = 28;
    const yWeek = 44;

    const yearsCount = endYear - startYear + 1;

    return (
      <g className="its-roadmap-axis">
        {Array.from({ length: yearsCount }).map((_, yi) => {
          const year = startYear + yi;
          const yearStartX = leftGutter + yearWidth * yi;

          return (
            <g key={`y-${year}`}>
              <text x={yearStartX + 10} y={yYear} className="axis-year">
                {year}
              </text>

              {FY_MONTHS.map((m, mi) => {
                const xC = monthCenterX(yearStartX, yearWidth, mi);
                const xS = monthStartX(yearStartX, yearWidth, mi);

                return (
                  <g key={`y-${year}-m-${mi}`}>
                    <text x={xC} y={yMonth} textAnchor="middle" className="axis-month">
                      {m.label}
                    </text>

                    <line
                      x1={xS}
                      y1={topGutter + axisHeight}
                      x2={xS}
                      y2={layout.svgHeight - 30}
                      className="grid-v"
                    />

                    {[1, 2, 3, 4].map((w) => {
                      const xW = weekCenterX(yearStartX, yearWidth, mi, w);
                      return (
                        <text
                          key={`y-${year}-m-${mi}-w-${w}`}
                          x={xW}
                          y={yWeek}
                          textAnchor="middle"
                          className="axis-week"
                        >
                          W{w}
                        </text>
                      );
                    })}

                    {[1, 2, 3].map((b) => {
                      const xSep = weekBoundaryX(yearStartX, yearWidth, mi, b);
                      return (
                        <line
                          key={`y-${year}-m-${mi}-tick-${b}`}
                          x1={xSep}
                          y1={topGutter + axisHeight - 16}
                          x2={xSep}
                          y2={topGutter + axisHeight - 6}
                          className="week-tick"
                        />
                      );
                    })}
                  </g>
                );
              })}

              <line
                x1={yearStartX + yearWidth}
                y1={topGutter + axisHeight}
                x2={yearStartX + yearWidth}
                y2={layout.svgHeight - 30}
                className="grid-v"
              />
            </g>
          );
        })}
      </g>
    );
  };

  const hoverLabel = useMemo(() => {
    if (!hoverNode?.title) return "";
    return hoverNode.title.length > 44 ? hoverNode.title.slice(0, 44) + "…" : hoverNode.title;
  }, [hoverNode]);

  const enabledCount = enabledLaneIds?.size || 0;

  const toggleLane = (laneId) => {
    setEnabledLaneIds((prev) => {
      const next = new Set(prev && prev.size ? Array.from(prev) : lanes.map((l) => l.id));
      if (next.has(laneId)) next.delete(laneId);
      else next.add(laneId);
      return next;
    });
  };

  const setAllLanes = (on) => {
    if (on) setEnabledLaneIds(new Set(lanes.map((l) => l.id)));
    else setEnabledLaneIds(new Set());
  };

  const zoomOut = () => setZoom((z) => clamp(Number((z - ZOOM_STEP).toFixed(2)), ZOOM_MIN, ZOOM_MAX));
  const zoomIn = () => setZoom((z) => clamp(Number((z + ZOOM_STEP).toFixed(2)), ZOOM_MIN, ZOOM_MAX));
  const zoomReset = () => setZoom(1);

  return (
    <>
      <OperationsHub />

      <main className="its-roadmap-page">
        {/* portal host so modal styles work */}
        <div id="its-roadmap-modal-root" />

        <div className="its-roadmap-top">
          <div>
            <div className="its-roadmap-breadcrumb">
              <a href="/operations-hub/">Overview</a> <span>›</span> <span>ITS Roadmap</span>
            </div>
            <h1 className="its-roadmap-title">ITS Roadmap</h1>
          </div>

          <div className="its-roadmap-controls">
            <div ref={searchWrapRef} className="its-roadmap-search">
              <span className="its-roadmap-searchIcon" aria-hidden="true">
                ⌕
              </span>
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSuggestOpen(true);
                  setActiveSuggest(0);
                }}
                onFocus={() => {
                  if (query.trim()) setSuggestOpen(true);
                }}
                onKeyDown={(e) => {
                  if (!suggestOpen || !suggestions.length) return;

                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveSuggest((v) => Math.min(v + 1, suggestions.length - 1));
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveSuggest((v) => Math.max(v - 1, 0));
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    chooseSuggestion(suggestions[activeSuggest]);
                  }
                  if (e.key === "Escape") {
                    setSuggestOpen(false);
                  }
                }}
                placeholder="Label"
              />

              {suggestOpen && suggestions.length ? (
                <div className="its-roadmap-suggestions" role="listbox">
                  {suggestions.map((s, i) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`its-roadmap-suggestItem${i === activeSuggest ? " is-active" : ""}`}
                      onMouseEnter={() => setActiveSuggest(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => chooseSuggestion(s)}
                    >
                      <span className="its-roadmap-suggestTitle">{s.title}</span>
                      <span className="its-roadmap-suggestMeta">
                        {s.laneLabel || s.lane} • {s.year}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <select
              className="its-roadmap-filterCtrl its-roadmap-year"
              value={jumpYear}
              onChange={(e) => setJumpYear(Number(e.target.value))}
              title="Jump to year"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <div ref={lanesWrapRef} className="its-roadmap-lanesWrap">
              <button
                className="its-roadmap-filterCtrl its-roadmap-lanesBtn"
                type="button"
                onClick={() => {
                  setLanesOpen((v) => !v);
                  setLegendOpen(false);
                }}
                title="Filter lanes"
              >
                Lanes ({enabledCount}/{lanes.length})
              </button>

              {lanesOpen && (
                <div className="its-roadmap-lanesPanel">
                  <div className="lanesPanel-head">
                    <div className="lanesPanel-title">Lanes</div>
                    <div className="lanesPanel-actions">
                      <button type="button" onClick={() => setAllLanes(true)}>
                        All
                      </button>
                      <button type="button" onClick={() => setAllLanes(false)}>
                        None
                      </button>
                    </div>
                  </div>

                  <div className="lanesPanel-list">
                    {lanes.map((l) => {
                      const checked = enabledLaneIds.size ? enabledLaneIds.has(l.id) : true;
                      return (
                        <label key={l.id} className="lanesPanel-item">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => toggleLane(l.id, e.target.checked)}
                          />
                          <span className="lanesPanel-pill">{l.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="its-roadmap-card">
          <div className="its-roadmap-legendWrap">
            <div className="its-roadmap-legendBtns">
              <div className="its-roadmap-zoomGroup" role="group" aria-label="Zoom">
                <button
                  type="button"
                  className="its-roadmap-zoomBtn"
                  onClick={zoomOut}
                  disabled={zoom <= ZOOM_MIN}
                  title="Zoom out"
                >
                  −
                </button>
                <button
                  type="button"
                  className="its-roadmap-zoomLabel"
                  onClick={zoomReset}
                  title="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  className="its-roadmap-zoomBtn"
                  onClick={zoomIn}
                  disabled={zoom >= ZOOM_MAX}
                  title="Zoom in"
                >
                  +
                </button>
              </div>

              <button
                className="its-roadmap-legendBtn"
                type="button"
                onClick={() => {
                  setLegendOpen((v) => !v);
                  setLanesOpen(false);
                }}
              >
                Legend <span aria-hidden="true">▾</span>
              </button>
            </div>

            {legendOpen && (
              <div className="its-roadmap-legend">
                <div className="its-roadmap-legendTitle">Milestones</div>
                <div className="its-roadmap-legendRow">
                  <span className="dot dot-major" /> Major
                </div>
                <div className="its-roadmap-legendRow">
                  <span className="dot dot-minor" /> Minor
                </div>

                <div className="its-roadmap-legendTitle">Paths</div>
                <div className="its-roadmap-legendRow">
                  <span className="line line-solid" /> Main
                </div>
                <div className="its-roadmap-legendRow">
                  <span className="line line-dashed" /> Supporting
                </div>
              </div>
            )}
          </div>

          <div
            ref={canvasRef}
            className="its-roadmap-canvas"
            style={{ "--axis-split": `${layout.topGutter + layout.axisHeight}px` }}
          >
            <div className="its-roadmap-laneOverlay">
              <div ref={laneOverlayRef} className="its-roadmap-laneOverlayInner">
                {layout.lanesDisplay.map((l) => {
                  const y = layout.topGutter + layout.axisHeight + (l.y - layout.baseLaneY) + 40;
                  return (
                    <div
                      key={l.id}
                      className={`lane-pill lane-pill-sticky${l.enabled ? "" : " lane-hidden"}`}
                      style={{ top: `${y - 14}px`, borderLeft: `4px solid ${l.color}`, color: l.color }}
                    >
                      {l.label}
                    </div>
                  );
                })}
              </div>
            </div>

            <svg
              className="its-roadmap-svg"
              viewBox={`0 0 ${layout.svgWidth} ${layout.svgHeight}`}
              width={layout.svgWidth * zoom}
              height={layout.svgHeight * zoom}
              preserveAspectRatio="xMinYMin meet"
            >
              <rect
                x="0"
                y="0"
                width={layout.svgWidth}
                height={layout.topGutter + layout.axisHeight}
                fill="#ffffff"
              />
              <rect
                x="0"
                y={layout.topGutter + layout.axisHeight}
                width={layout.svgWidth}
                height={layout.svgHeight - (layout.topGutter + layout.axisHeight)}
                fill="#F5F6F8"
              />

              <line
                x1="0"
                y1={layout.topGutter + layout.axisHeight}
                x2={layout.svgWidth}
                y2={layout.topGutter + layout.axisHeight}
                className="axis-divider"
              />

              <Axis />

              <g className="its-roadmap-lanes">
                {layout.lanesDisplay.map((l, i) => {
                  const y = layout.topGutter + layout.axisHeight + (l.y - layout.baseLaneY) + 40;
                  const bandTop = y - 40;

                  return (
                    <g key={l.id} className={l.enabled ? "" : "lane-hidden"}>
                      <rect
                        x={layout.leftGutter}
                        y={bandTop}
                        width={layout.svgWidth - layout.leftGutter - layout.rightPad}
                        height={80}
                        className={i % 2 === 0 ? "lane-band lane-band-a" : "lane-band lane-band-b"}
                      />
                      <line
                        x1={layout.leftGutter}
                        y1={y}
                        x2={layout.svgWidth - layout.rightPad}
                        y2={y}
                        className="lane-line"
                      />
                    </g>
                  );
                })}
              </g>

              <g className="its-roadmap-edges">
                {sortedEdges.map((e) => {
                  const dim = hoverNode && related && !related.edgeIds.has(e.id);
                  return (
                    <path
                      key={e.id}
                      d={curvePath(e.from.x, e.from.y, e.to.x, e.to.y)}
                      style={{ stroke: e.to.color }}
                      className={[
                        e.dashed ? "edge edge-dashed" : "edge edge-solid",
                        dim ? "is-dim" : "is-on",
                      ].join(" ")}
                    />
                  );
                })}
              </g>

              <g className="its-roadmap-nodes">
                {layout.nodes.map((n) => {
                  const dim = hoverNode && related && !related.nodeIds.has(n.id);
                  const hot = hoverNode && n.id === hoverNode.id;
                  return (
                    <g
                      key={n.id}
                      transform={`translate(${n.x} ${n.y})`}
                      className={[
                        "node",
                        dim ? "is-dim" : "is-on",
                        highlightNodeId === n.id ? "is-highlight" : "",
                      ].join(" ")}
                      onPointerEnter={() => setHoverNode(n)}
                      onPointerLeave={() => setHoverNode(null)}
                      onClick={() => setSelectedNode(n)}
                    >
                      <circle
                        r={n.r + (hot ? 2 : 0)}
                        fill={n.color}
                        stroke="#ffffff"
                        strokeWidth="3"
                      />
                    </g>
                  );
                })}
              </g>

              {hoverNode ? (
                <g
                  className="its-roadmap-tooltip"
                  transform={`translate(${hoverNode.x + 14} ${hoverNode.y - 10})`}
                >
                  <rect
                    className="tooltip-bg"
                    x="0"
                    y="-22"
                    rx="8"
                    ry="8"
                    width="320"
                    height="44"
                  />
                  <text className="tooltip-text" x="12" y="4">
                    {hoverLabel}
                  </text>
                </g>
              ) : null}
            </svg>
          </div>
        </section>

        <Modal open={!!selectedNode} node={selectedNode} onClose={() => setSelectedNode(null)} />
      </main>
    </>
  );
}
